import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ApiService from '../../services/ApiService';
import Loader from '../../components/Loader';
import { formatDate, formatCurrency, formatDateLocal, formatTime } from '../../utils/helper';
import { toast } from 'react-toastify';
import { getAppointmentStatus } from '../../utils/statusHelper';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';

const Details = () => {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [visit, setVisit] = useState(null);
    const [recommendedPatients, setRecommendedPatients] = useState([]);
    const [scheduledAppointments, setScheduledAppointments] = useState([]);
    const [selectedPatients, setSelectedPatients] = useState([]);
    const [slotDuration, setSlotDuration] = useState(10);
    const [isGenerating, setIsGenerating] = useState(false);

    // New Booking Modal State
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [patientType, setPatientType] = useState('existing'); // 'existing' or 'new'
    const [patients, setPatients] = useState([]);
    const [insuranceTypes, setInsuranceTypes] = useState([]);
    const [selectedPatientId, setSelectedPatientId] = useState(null);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [newPatientData, setNewPatientData] = useState({
        name: '',
        phone: '',
        email: '',
        gender: 'male',
        address: '',
        date_of_birth: '',
        insurance_type_id: '',
        insurance_card_number: ''
    });
    const [isSavingBooking, setIsSavingBooking] = useState(false);
    const [selectedTime, setSelectedTime] = useState(null);

    useEffect(() => {
        fetchDetails();
        fetchPatients();
        fetchInsuranceTypes();
    }, [id]);

    const fetchInsuranceTypes = async () => {
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/insurance-types/all',
            });
            if (response.data.status) {
                setInsuranceTypes(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching insurance types:', error);
        }
    };

    useEffect(() => {
        if (selectedPatientId) {
            const patient = patients.find(p => p.id === selectedPatientId);
            setSelectedPatient(patient);
        } else {
            setSelectedPatient(null);
        }
    }, [selectedPatientId, patients]);

    const fetchPatients = async () => {
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/patients',
                data: { per_page: 1000 }
            });
            if (response.data.status) {
                const formattedPatients = (response.data.data.patients || []).map(p => ({
                    ...p,
                    label: `${p.name} (${p.phone})`
                }));
                setPatients(formattedPatients);
            }
        } catch (error) {
            console.error('Error fetching patients:', error);
        }
    };

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: `admin/specialist-visits/${id}`,
            });
            if (response.data.status) {
                setVisit(response.data.data.visit);
                setRecommendedPatients(response.data.data.recommended_patients || []);
                setScheduledAppointments(response.data.data.scheduled_appointments || []);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error fetching details');
        } finally {
            setLoading(false);
        }
    };

    // Parse a time string ("HH:MM", "HH:MM:SS" or "h:MM AM/PM") into minutes from midnight
    const parseTimeToMinutes = (timeStr) => {
        if (!timeStr) return null;
        const str = String(timeStr).trim();
        const ampm = str.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (ampm) {
            let h = parseInt(ampm[1], 10);
            const m = parseInt(ampm[2], 10);
            const ap = ampm[3].toUpperCase();
            if (ap === 'PM' && h !== 12) h += 12;
            if (ap === 'AM' && h === 12) h = 0;
            return h * 60 + m;
        }
        const match = str.match(/(\d{1,2}):(\d{2})/);
        if (match) return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
        return null;
    };

    const minutesToValue = (mins) => `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;

    const minutesToLabel = (mins) => {
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        const ap = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 === 0 ? 12 : h % 12;
        return `${h12}:${String(m).padStart(2, '0')} ${ap}`;
    };

    // Build available time slots from the visit window, excluding already-booked times
    const availableSlots = useMemo(() => {
        if (!visit) return [];
        const start = parseTimeToMinutes(visit.visit_time_from);
        const end = parseTimeToMinutes(visit.visit_time_to);
        const dur = parseInt(slotDuration, 10) || 10;
        if (start == null || end == null || dur <= 0 || start >= end) return [];

        const booked = new Set(
            scheduledAppointments.map(a => parseTimeToMinutes(a.time)).filter(v => v != null)
        );

        const slots = [];
        for (let t = start; t < end; t += dur) {
            if (!booked.has(t)) {
                slots.push({ value: minutesToValue(t), label: minutesToLabel(t) });
            }
        }
        return slots;
    }, [visit, scheduledAppointments, slotDuration]);

    const handleSelectPatient = (patientId) => {
        if (selectedPatients.includes(patientId)) {
            setSelectedPatients(selectedPatients.filter(id => id !== patientId));
        } else {
            setSelectedPatients([...selectedPatients, patientId]);
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedPatients(recommendedPatients.map(p => p.patient_id));
        } else {
            setSelectedPatients([]);
        }
    };

    const generateAppointments = async () => {
        if (selectedPatients.length === 0) {
            toast.error('Please select at least one patient.');
            return;
        }

        if (!window.confirm(`Create appointments for ${selectedPatients.length} patients with ${slotDuration} min slots?`)) return;

        setIsGenerating(true);
        try {
            const response = await ApiService.request({
                method: 'POST',
                url: `admin/specialist-visits/${id}/generate-appointments`,
                data: {
                    patient_ids: selectedPatients,
                    slot_duration: slotDuration
                }
            });
            if (response.data.status) {
                toast.success(response.data.message);
                fetchDetails(); // Refresh to show scheduled appointments
                setSelectedPatients([]);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error generating appointments');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleNewAppointment = async (e) => {
        e.preventDefault();
        
        if (patientType === 'existing' && !selectedPatientId) {
            toast.error('Please select a patient.');
            return;
        }
        if (patientType === 'new' && (!newPatientData.name || !newPatientData.phone)) {
            toast.error('Please fill in required patient details (Name & Phone).');
            return;
        }
        if (!selectedTime) {
            toast.error('Please select a time slot.');
            return;
        }

        setIsSavingBooking(true);
        try {
            const response = await ApiService.request({
                method: 'POST',
                url: `admin/specialist-visits/${id}/new-appointment`,
                data: {
                    patient_type: patientType,
                    patient_id: selectedPatientId,
                    new_patient: newPatientData,
                    time: selectedTime
                }
            });
            if (response.data.status) {
                toast.success(response.data.message);
                setShowBookingModal(false);
                fetchDetails(); // Refresh appointments
                // Reset form
                setSelectedTime(null);
                setSelectedPatientId(null);
                setNewPatientData({
                    name: '',
                    phone: '',
                    email: '',
                    gender: 'male',
                    address: '',
                    date_of_birth: '',
                    insurance_type_id: '',
                    insurance_card_number: ''
                });
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error creating appointment');
        } finally {
            setIsSavingBooking(false);
        }
    };

    if (loading) return <Loader />;
    if (!visit) return <div>Visit not found</div>;

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Visit Details
                        </h1>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <button 
                            className="btn btn-sm fw-bold btn-primary"
                            onClick={() => setShowBookingModal(true)}
                        >
                            <i className="fas fa-plus me-1"></i> New Appointment
                        </button>
                        <Link to="/specialist-visits" className="btn btn-sm fw-bold btn-secondary">
                            Back to List
                        </Link>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    
                    {/* Visit Info Card */}
                    <div className="card mb-5 mb-xl-10">
                        <div className="card-header cursor-pointer">
                            <div className="card-title m-0">
                                <h3 className="fw-bold m-0">Specialist Visit Information</h3>
                            </div>
                        </div>
                        <div className="card-body p-9">
                            <div className="row mb-7">
                                <label className="col-lg-4 fw-semibold text-muted">Specialist Name</label>
                                <div className="col-lg-8">
                                    <span className="fw-bold fs-6 text-gray-800">{visit.specialist?.name}</span>
                                </div>
                            </div>
                            <div className="row mb-7">
                                <label className="col-lg-4 fw-semibold text-muted">Contact</label>
                                <div className="col-lg-8">
                                    <span className="fw-bold fs-6 text-gray-800">
                                        {visit.specialist?.phone} / {visit.specialist?.email}
                                    </span>
                                </div>
                            </div>
                            <div className="row mb-7">
                                <label className="col-lg-4 fw-semibold text-muted">Date & Time</label>
                                <div className="col-lg-8">
                                    <span className="fw-bold fs-6 text-gray-800">
                                        {formatDate(visit.visit_date)} ({formatTime(visit.visit_time_from)} - {formatTime(visit.visit_time_to)})
                                    </span>
                                </div>
                            </div>
                            <div className="row mb-7">
                                <label className="col-lg-4 fw-semibold text-muted">Clinic Fee</label>
                                <div className="col-lg-8">
                                    <span className="fw-bold fs-6 text-gray-800">{formatCurrency(visit.clinic_fee)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Recommended Patients Card */}
                    <div className="card mb-5 mb-xl-10">
                         <div className="card-header border-0 pt-6">
                            <div className="card-title">
                                <h3 className="fw-bold m-0">Recommended Patients</h3>
                            </div>
                            <div className="card-toolbar">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="input-group input-group-sm w-150px">
                                        <span className="input-group-text">Slot Mins</span>
                                        <input 
                                            type="number" 
                                            className="form-control" 
                                            value={slotDuration} 
                                            onChange={(e) => setSlotDuration(e.target.value)} 
                                            min="5"
                                        />
                                    </div>
                                    <button 
                                        className="btn btn-sm btn-primary" 
                                        onClick={generateAppointments}
                                        disabled={isGenerating || selectedPatients.length === 0}
                                    >
                                        {isGenerating ? 'Generating...' : 'Create Appointments'}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="card-body p-9">
                            <div className="table-responsive">
                                <table className="table align-middle table-row-dashed fs-6 gy-5">
                                    <thead>
                                        <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                            <th className="w-10px pe-2">
                                                <div className="form-check form-check-sm form-check-custom form-check-solid me-3">
                                                    <input 
                                                        className="form-check-input" 
                                                        type="checkbox" 
                                                        onChange={handleSelectAll}
                                                        checked={recommendedPatients.length > 0 && selectedPatients.length === recommendedPatients.length}
                                                    />
                                                </div>
                                            </th>
                                            <th className="min-w-150px">Patient Name</th>
                                            <th className="min-w-125px">Phone</th>
                                            <th className="min-w-125px">Referred By (Doctor)</th>
                                            <th className="min-w-125px">Next Visit Date</th>
                                            <th className="min-w-200px">Doctor Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-gray-600 fw-semibold">
                                        {recommendedPatients.map((patient, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <div className="form-check form-check-sm form-check-custom form-check-solid">
                                                        <input 
                                                            className="form-check-input" 
                                                            type="checkbox" 
                                                            checked={selectedPatients.includes(patient.patient_id)}
                                                            onChange={() => handleSelectPatient(patient.patient_id)}
                                                        />
                                                    </div>
                                                </td>
                                                <td>{patient.patient_name}</td>
                                                <td>{patient.patient_phone}</td>
                                                <td>{patient.doctor_name}</td>
                                                <td>{formatDate(patient.next_visit,'MMM, YYYY')}</td>
                                                <td>{patient.notes}</td>
                                            </tr>
                                        ))}
                                        {recommendedPatients.length === 0 && (
                                            <tr>
                                                <td colSpan="6" className="text-center">No recommended patients found for this month.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Scheduled Appointments Card */}
                    <div className="card mb-5 mb-xl-10">
                        <div className="card-header cursor-pointer">
                            <div className="card-title m-0">
                                <h3 className="fw-bold m-0">Scheduled Appointments</h3>
                            </div>
                        </div>
                        <div className="card-body p-9">
                            <div className="table-responsive">
                                <table className="table align-middle table-row-dashed fs-6 gy-5">
                                    <thead>
                                        <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                            <th className="min-w-100px">Ref No</th>
                                            <th className="min-w-125px">Time</th>
                                            <th className="min-w-150px">Patient Name</th>
                                            <th className="min-w-125px">Phone</th>
                                            <th className="min-w-100px">Status</th>
                                            <th className="text-end min-w-100px">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-gray-600 fw-semibold">
                                        {scheduledAppointments.map((apt, index) => (
                                            <tr key={index}>
                                                <td>{apt.ref_no}</td>
                                                <td>{formatTime(apt.time)}</td>
                                                <td>{apt.patient?.name}</td>
                                                <td>{apt.patient?.phone}</td>
                                                <td>
                                                     <span className={`badge ${getAppointmentStatus(apt.status).badgeClass}`}>
                                                        {getAppointmentStatus(apt.status).label}
                                                    </span>
                                                </td>
                                                <td className="text-end">
                                                    <Link to={`/specialist-checkup/${apt.id}`} className="btn btn-sm btn-light btn-active-light-primary">
                                                         Checkup Details
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                        {scheduledAppointments.length === 0 && (
                                            <tr>
                                                <td colSpan="6" className="text-center">No scheduled appointments yet.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <Dialog 
                header="New Specialist Appointment" 
                visible={showBookingModal} 
                style={{ width: '55vw' }} 
                onHide={() => setShowBookingModal(false)}
            >
                <form onSubmit={handleNewAppointment}>
                    <div className="mb-7">
                        <label className="fs-6 fw-bold mb-2">Patient Type</label>
                        <div className="d-flex align-items-center gap-5">
                            <div className="form-check form-check-custom form-check-solid">
                                <input 
                                    className="form-check-input" 
                                    type="radio" 
                                    value="existing" 
                                    checked={patientType === 'existing'} 
                                    onChange={(e) => setPatientType(e.target.value)} 
                                />
                                <label className="form-check-label fw-bold">Existing Patient</label>
                            </div>
                            <div className="form-check form-check-custom form-check-solid">
                                <input 
                                    className="form-check-input" 
                                    type="radio" 
                                    value="new" 
                                    checked={patientType === 'new'} 
                                    onChange={(e) => setPatientType(e.target.value)} 
                                />
                                <label className="form-check-label fw-bold">New Patient</label>
                            </div>
                        </div>
                    </div>

                    {patientType === 'existing' ? (
                        <>
                            <div className="mb-7">
                                <label className="fs-6 fw-bold mb-2">Select Patient</label>
                                <Dropdown 
                                    value={selectedPatientId} 
                                    options={patients} 
                                    onChange={(e) => setSelectedPatientId(e.value)} 
                                    optionLabel="label" 
                                    optionValue="id"
                                    filter 
                                    placeholder="Search for a patient" 
                                    className="w-100" 
                                />
                            </div>
                            {selectedPatient && (
                                <div className="card bg-light p-5 mb-7">
                                    <div className="row g-5">
                                        <div className="col-md-6">
                                            <div className="fw-bold text-muted mb-1">Name</div>
                                            <div className="fw-bold text-gray-800">{selectedPatient.name}</div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="fw-bold text-muted mb-1">Phone</div>
                                            <div className="fw-bold text-gray-800">{selectedPatient.phone}</div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="fw-bold text-muted mb-1">Insurance</div>
                                            <div className="fw-bold text-gray-800">{selectedPatient.insurance_type?.title || 'N/A'}</div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="fw-bold text-muted mb-1">Insurance Card</div>
                                            <div className="fw-bold text-gray-800">{selectedPatient.insurance_card_number || 'N/A'}</div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="row g-9 mb-7">
                                <div className="col-md-6 fv-row">
                                    <label className="required fs-6 fw-bold mb-2">Name</label>
                                    <input 
                                        type="text" 
                                        className="form-control form-control-solid" 
                                        value={newPatientData.name} 
                                        onChange={(e) => setNewPatientData({...newPatientData, name: e.target.value})} 
                                        required 
                                    />
                                </div>
                                <div className="col-md-6 fv-row">
                                    <label className="fs-6 fw-bold mb-2">Email</label>
                                    <input 
                                        type="email" 
                                        className="form-control form-control-solid" 
                                        value={newPatientData.email} 
                                        onChange={(e) => setNewPatientData({...newPatientData, email: e.target.value})} 
                                    />
                                </div>
                            </div>
                            <div className="row g-9 mb-7">
                                <div className="col-md-6 fv-row">
                                    <label className="required fs-6 fw-bold mb-2">Phone</label>
                                    <div className="input-group input-group-solid">
                                        <span className="input-group-text">+255</span>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            placeholder="Phone Number"
                                            value={newPatientData.phone} 
                                            onChange={(e) => setNewPatientData({...newPatientData, phone: e.target.value})} 
                                            required 
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6 fv-row">
                                    <label className="fs-6 fw-bold mb-2">Insurance Type (Optional)</label>
                                    <Dropdown 
                                        value={newPatientData.insurance_type_id} 
                                        options={insuranceTypes} 
                                        onChange={(e) => setNewPatientData({...newPatientData, insurance_type_id: e.value})} 
                                        optionLabel="title" 
                                        optionValue="id"
                                        filter 
                                        placeholder="Select Insurance" 
                                        className="w-100" 
                                    />
                                </div>
                            </div>
                            <div className="row g-9 mb-7">
                                <div className="col-md-6 fv-row">
                                    <label className={`fs-6 fw-bold mb-2 ${newPatientData.insurance_type_id ? 'required' : ''}`}>Insurance Card Number</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-solid"
                                        value={newPatientData.insurance_card_number}
                                        onChange={(e) => setNewPatientData({...newPatientData, insurance_card_number: e.target.value})}
                                        required={!!newPatientData.insurance_type_id}
                                    />
                                </div>
                                <div className="col-md-6 fv-row">
                                    <label className="fs-6 fw-bold mb-2">Gender</label>
                                    <select 
                                        className="form-select form-select-solid" 
                                        value={newPatientData.gender} 
                                        onChange={(e) => setNewPatientData({...newPatientData, gender: e.target.value})}
                                    >
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>
                                    </select>
                                </div>
                            </div>
                            <div className="row g-9 mb-7">
                                <div className="col-md-6 fv-row">
                                    <label className="fs-6 fw-bold mb-2">Date of Birth</label>
                                    <input 
                                        type="date" 
                                        className="form-control form-control-solid" 
                                        value={newPatientData.date_of_birth} 
                                        onChange={(e) => setNewPatientData({...newPatientData, date_of_birth: e.target.value})} 
                                    />
                                </div>
                                <div className="col-md-6 fv-row">
                                    <label className="fs-6 fw-bold mb-2">Address</label>
                                    <input 
                                        type="text" 
                                        className="form-control form-control-solid" 
                                        value={newPatientData.address} 
                                        onChange={(e) => setNewPatientData({...newPatientData, address: e.target.value})} 
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    <div className="mb-7">
                        <label className="required fs-6 fw-bold mb-2">Appointment Time Slot</label>
                        <div className="text-muted fs-7 mb-3">
                            Visit hours: {formatTime(visit.visit_time_from)} - {formatTime(visit.visit_time_to)}
                        </div>
                        {availableSlots.length === 0 ? (
                            <div className="text-danger fw-semibold">No available time slots.</div>
                        ) : (
                            <div
                                className="d-flex flex-wrap gap-2 overflow-auto border border-gray-300 rounded p-3"
                                style={{ maxHeight: '220px' }}
                            >
                                {availableSlots.map(slot => (
                                    <button
                                        type="button"
                                        key={slot.value}
                                        className={`btn btn-sm ${selectedTime === slot.value ? 'btn-primary' : 'btn-light-primary'}`}
                                        onClick={() => setSelectedTime(slot.value)}
                                    >
                                        {slot.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="text-center pt-10">
                        <button
                            type="button"
                            className="btn btn-light me-3"
                            onClick={() => setShowBookingModal(false)}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className="btn btn-primary" 
                            disabled={isSavingBooking}
                        >
                            {isSavingBooking ? 'Saving...' : 'Save Appointment'}
                        </button>
                    </div>
                </form>
            </Dialog>
        </div>
    );
};

export default Details;
